
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContractService } from '@/lib/types/services';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Coins } from 'lucide-react';

interface ContractServicesCardProps {
  services: ContractService[];
}

export function ContractServicesCard({ services }: ContractServicesCardProps) {
  const { t } = useTranslation();

  if (!services || services.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('contractServices')}</CardTitle>
          <CardDescription>{t('servicesAssociated')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            <p>{t('noServices')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const calculateTotalCost = (service: ContractService) => {
    return (service.service?.cost || 0) * service.quantity;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>{t('contractServices')}</CardTitle>
            <CardDescription>{t('servicesAssociated')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('serviceName')}</TableHead>
              <TableHead>{t('serviceType')}</TableHead>
              <TableHead>{t('quantity')}</TableHead>
              <TableHead>{t('unitCost')}</TableHead>
              <TableHead>{t('totalCost')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((contractService) => (
              <TableRow key={contractService.id}>
                <TableCell>{contractService.service?.name}</TableCell>
                <TableCell className="capitalize">
                  {contractService.service?.service_type && 
                    t(`serviceTypes.${contractService.service.service_type.toLowerCase()}`)}
                </TableCell>
                <TableCell>{contractService.quantity}</TableCell>
                <TableCell>
                  {contractService.service?.cost.toLocaleString()} 
                  {contractService.service?.currency?.symbol}
                </TableCell>
                <TableCell>
                  {calculateTotalCost(contractService).toLocaleString()} 
                  {contractService.service?.currency?.symbol}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
